import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useToast } from "@/hooks/use-toast";

export type PlanTier = "starter" | "professional" | "enterprise";

export interface PlanConfig {
  tier: PlanTier;
  name: string;
  price: number;
  priceLabel: string;
  maxCompanies: number | null;
  maxUsers: number | null;
  features: string[];
}

export const PLANS: Record<PlanTier, PlanConfig> = {
  starter: {
    tier: "starter",
    name: "Starter",
    price: 499,
    priceLabel: "₹499",
    maxCompanies: 1,
    maxUsers: 2,
    features: [
      "1 Company",
      "2 Accountants",
      "Basic Reports",
      "Invoice Management",
      "Ledger & Journal",
      "Email Support",
    ],
  },
  professional: {
    tier: "professional",
    name: "Professional",
    price: 999,
    priceLabel: "₹999",
    maxCompanies: 3,
    maxUsers: 5,
    features: [
      "3 Companies",
      "5 Accountants",
      "GST Reports",
      "Inventory Management",
      "E-Way Billing",
      "Multi Invoice Templates",
      "CA Collaboration",
      "Priority Email Support",
    ],
  },
  enterprise: {
    tier: "enterprise",
    name: "Enterprise",
    price: 1999,
    priceLabel: "₹1,999",
    maxCompanies: null,
    maxUsers: null,
    features: [
      "Unlimited Companies",
      "Unlimited Users",
      "Advanced Analytics",
      "API Access",
      "Custom Templates",
      "Audit Logs",
      "Priority Support",
      "SSO & Security",
    ],
  },
};

export interface Subscription {
  plan: PlanTier;
  status: "trial" | "active" | "expired" | "cancelled";
  trialStartDate: Date;
  trialEndDate: Date;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  daysRemaining: number;
  isTrialActive: boolean;
}

interface SubscriptionContextType {
  subscription: Subscription;
  plans: typeof PLANS;
  upgradePlan: (tier: PlanTier) => void;
  cancelSubscription: () => void;
  renewSubscription: () => void;
}

function createTrialSubscription(): Subscription {
  const now = new Date();
  const trialStart = new Date(now);
  trialStart.setDate(trialStart.getDate() - 2); // started 2 days ago
  const trialEnd = new Date(trialStart);
  trialEnd.setMonth(trialEnd.getMonth() + 4); // 4-month trial

  const diffMs = trialEnd.getTime() - now.getTime();
  const daysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));

  return {
    plan: "professional",
    status: "trial",
    trialStartDate: trialStart,
    trialEndDate: trialEnd,
    currentPeriodStart: trialStart,
    currentPeriodEnd: trialEnd,
    daysRemaining,
    isTrialActive: daysRemaining > 0,
  };
}

const SubscriptionContext = createContext<SubscriptionContextType | null>(null);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const [subscription, setSubscription] = useState<Subscription>(createTrialSubscription);
  const { toast } = useToast();

  // Renewal reminders
  useEffect(() => {
    if (subscription.status === "trial") {
      if (subscription.daysRemaining <= 7 && subscription.daysRemaining > 0) {
        toast({
          title: "Trial Ending Soon",
          description: `Your free trial ends in ${subscription.daysRemaining} days. Choose a plan to continue using AccouPro.`,
          variant: "destructive",
        });
      }
    } else if (subscription.status === "active") {
      const renewalMs = subscription.currentPeriodEnd.getTime() - Date.now();
      const renewalDays = Math.ceil(renewalMs / (1000 * 60 * 60 * 24));
      if (renewalDays <= 5 && renewalDays > 0) {
        toast({
          title: "Renewal Reminder",
          description: `Your ${PLANS[subscription.plan].name} plan renews in ${renewalDays} days.`,
        });
      }
    }
  }, [subscription.status]);

  const upgradePlan = (tier: PlanTier) => {
    const now = new Date();
    const periodEnd = new Date(now);
    periodEnd.setMonth(periodEnd.getMonth() + 1);

    setSubscription((prev) => ({
      ...prev,
      plan: tier,
      status: "active",
      currentPeriodStart: now,
      currentPeriodEnd: periodEnd,
      daysRemaining: 30,
      isTrialActive: false,
    }));

    toast({
      title: "Plan Upgraded!",
      description: `You're now on the ${PLANS[tier].name} plan at ${PLANS[tier].priceLabel}/month.`,
    });
  };

  const cancelSubscription = () => {
    setSubscription((prev) => ({ ...prev, status: "cancelled" }));
    toast({
      title: "Subscription Cancelled",
      description: "Your access continues until the end of the current billing period.",
      variant: "destructive",
    });
  };

  const renewSubscription = () => {
    const now = new Date();
    const periodEnd = new Date(now);
    periodEnd.setMonth(periodEnd.getMonth() + 1);

    setSubscription((prev) => ({
      ...prev,
      status: "active",
      currentPeriodStart: now,
      currentPeriodEnd: periodEnd,
      daysRemaining: 30,
    }));

    toast({
      title: "Subscription Renewed",
      description: `Your ${PLANS[subscription.plan].name} plan has been renewed.`,
    });
  };

  return (
    <SubscriptionContext.Provider value={{ subscription, plans: PLANS, upgradePlan, cancelSubscription, renewSubscription }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
}
